import * as React from 'react';
import { EyeIcon } from '@phosphor-icons/react/Eye';
import { EyeSlashIcon } from '@phosphor-icons/react/EyeSlash';
import { cn } from '../../../utils/cn';
import { Input } from '../input';

export interface PasswordInputProps extends Omit<React.ComponentProps<'input'>, 'type'> {
  /**
   * Test id for the inner input; the toggle button gets
   * `${data-testid}-toggle`.
   */
  'data-testid'?: string;
}

const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, disabled, 'data-testid': dataTestId = 'password-input', ...props }, ref) => {
    const innerRef = React.useRef<HTMLInputElement>(null);
    const selectionRef = React.useRef<[number, number] | null>(null);
    const [isVisible, setIsVisible] = React.useState(false);

    const inputType = isVisible ? 'text' : 'password';
    const toggleLabel = isVisible ? 'Hide password' : 'Show password';
    const ToggleIcon = isVisible ? EyeSlashIcon : EyeIcon;

    const handleToggleMouseDown = React.useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        // Keep focus (and the caret) in the input when toggling with the mouse.
        event.preventDefault();
      },
      [],
    );

    const handleToggleClick = React.useCallback(() => {
      const input = innerRef.current;
      const hasFocusedSelection =
        input !== null && document.activeElement === input && input.selectionStart !== null;
      if (hasFocusedSelection)
        selectionRef.current = [input.selectionStart ?? 0, input.selectionEnd ?? 0];
      setIsVisible((prev) => !prev);
    }, []);

    React.useImperativeHandle(ref, () => innerRef.current as HTMLInputElement, []);

    // Revealed state must not survive a submit attempt (e.g. a failed login).
    React.useEffect(() => {
      const form = innerRef.current?.form;
      if (!form) return;
      const handleFormSubmit = () => setIsVisible(false);
      form.addEventListener('submit', handleFormSubmit);
      return () => form.removeEventListener('submit', handleFormSubmit);
    }, []);

    // Browsers reset the caret when the input type flips; restore it.
    React.useLayoutEffect(() => {
      const input = innerRef.current;
      const selection = selectionRef.current;
      selectionRef.current = null;
      if (!input || !selection) return;
      input.setSelectionRange(selection[0], selection[1]);
    }, [isVisible]);

    return (
      <div className="relative">
        <Input
          type={inputType}
          className={cn('pr-10', className)}
          disabled={disabled}
          ref={innerRef}
          data-testid={dataTestId}
          {...props}
        />
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground focus-visible:ring-ring absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-md focus-visible:ring-1 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          aria-label={toggleLabel}
          disabled={disabled}
          onMouseDown={handleToggleMouseDown}
          onClick={handleToggleClick}
          data-testid={`${dataTestId}-toggle`}
        >
          <ToggleIcon
            className="size-4"
            aria-hidden="true"
            weight="bold"
          />
        </button>
      </div>
    );
  },
);
PasswordInput.displayName = 'PasswordInput';

export { PasswordInput };
